import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';

import { SPAN_REMOVE_CASHBACK, SPAN_SELECT_CASHBACK } from '@constants/otel';
import { paths } from '@constants/paths';
import { ProductType } from '@generated/gql/graphql';
import { useDebouncedIsLoading } from '@hooks/useDebouncedIsLoading';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useUpdatedTariffModalContext } from '@providers/basket/UpdatedTariffModalProvider/Context';
import { useTrace } from '@services/otel';
import { UpdatedTariffModalVariant } from '@src/types/bundle';
import { deselectQuote } from '@utils/queries/quotesSelection';

import { selectCbcQuoteMutation } from './query';
import { useTrackCbcSelected } from './useTrackCbcSelected';

type UseCashbackHandlerParams = {
  quoteId: string;
  isSelected: boolean;
};

export const useCashbackHandler = ({
  quoteId,
  isSelected,
}: UseCashbackHandlerParams) => {
  const { withTrace } = useTrace();
  const navigate = useNavigate();
  const setSnackbarError = useSnackbarError();
  const { setModalState } = useUpdatedTariffModalContext();
  const { basketId } = useBasketContext();
  const { trackCbcSelected } = useTrackCbcSelected();

  const [selectCbc, { loading: selectLoading }] = useMutation(
    selectCbcQuoteMutation
  );
  const [deselectCbc, { loading: deselectLoading }] =
    useMutation(deselectQuote);

  const isLoading = useDebouncedIsLoading(
    [selectLoading, deselectLoading],
    false
  );

  const executeSelectCashback = async () => {
    try {
      await selectCbc({
        variables: {
          input: {
            quoteId,
            basketId,
          },
        },
        onCompleted: trackCbcSelected,
      });

      setModalState(UpdatedTariffModalVariant.productAdded);
      navigate(paths.bundle);
    } catch (error) {
      setSnackbarError(error as ErrorLike);
    }
  };

  const selectCashback = () => {
    if (isSelected) {
      setModalState(UpdatedTariffModalVariant.productAdded);
      navigate(paths.bundle);
      return;
    }

    return withTrace(SPAN_SELECT_CASHBACK, executeSelectCashback, { quoteId });
  };

  const executeRemoveCashback = async () => {
    try {
      await deselectCbc({
        variables: {
          quoteInput: {
            quoteId,
            basketId,
          },
        },
      });

      setModalState(UpdatedTariffModalVariant.productRemoved, [
        ProductType.CashbackCard,
      ]);
      navigate(paths.bundle);
    } catch (error) {
      setSnackbarError(error as ErrorLike);
    }
  };

  const removeCashback = () =>
    withTrace(SPAN_REMOVE_CASHBACK, executeRemoveCashback, { quoteId });

  return {
    selectCashback,
    removeCashback,
    isLoading,
    isRemoving: deselectLoading,
  };
};
